// src/oura/components/ViewAsBanner.tsx
//
// The merchant shell's answer to "staff are looking at this" (#4112). The
// backend makes a view-as session read-only (ourteam-backend#5712): every write
// is refused 403 `view_as_read_only` and the token lasts about two hours. So the
// screen says it once, quietly, in the bar across the top, and the controls
// that write step aside (`useViewAs`). Nothing renders for a real merchant.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRightIcon, EyeIcon } from "lucide-react";
import { useUserProfileContext } from "@context/user_profile";
import { useSnackbar } from "@hook/use-snackbar";
import { useViewAs } from "@hook/use-view-as";
import { endExpiredSession } from "@util/session";
import { VIEW_AS_REFUSED_EVENT } from "@util/viewAs";
import { MERCHANT_TOPBAR_ID } from "./ScanVersions";
import { OURA_ROOT } from "../nav";
import { isOuraProd, setDevOuraRole, useOuraRole } from "../role";

/** The sentence the API writes for a refused write, used only if it sent none. */
const REFUSAL_FALLBACK = "You are viewing as the client; this screen is read-only.";

/**
 * Staff looking at the merchant's screens, said once in a thin bar pinned
 * across the top of the content, like the past-scan bar (PastScanBar) and the
 * Trainer's status bars, with the way back to the staff view beside it.
 *
 * Two kinds of preview reach here. A "View as the merchant" session runs in
 * its own tab on the merchant's subdomain, opened from the staff tab, so the
 * way back is closing it: the staff tab is still open behind it. The dev role
 * cookie (dev and staging) previews in place, so the way back is switching the
 * cookie to staff. Nothing renders for a real merchant.
 */
export function ViewAsBanner({ businessName }: { businessName: string | null }) {
    const viewAs = useViewAs();
    const role = useOuraRole();
    const [profileLoading, userInfo] = useUserProfileContext();
    const [slot, setSlot] = useState<HTMLElement | null>(null);
    // window.close() only works on a tab a script opened. When it is refused,
    // say what to do instead of a button that does nothing.
    const [closeRefused, setCloseRefused] = useState(false);
    useEffect(() => setSlot(document.getElementById(MERCHANT_TOPBAR_ID)), []);
    // The two hours are up: end the tab now rather than leave the merchant's data
    // on screen until some later request happens to 401. It reads the impersonation
    // claim before clearing the cookie, so the login says to reopen the view.
    useEffect(() => {
        if (viewAs.ended) endExpiredSession();
    }, [viewAs.ended]);

    // The dev cookie makes a staff login read as the merchant. Only once the
    // profile has loaded, so a real merchant never sees the bar flash.
    const devPreview = !viewAs.active && !isOuraProd() && role === "oura-client" && !profileLoading && !!userInfo && userInfo.role !== "oura-client";
    if (!viewAs.active && !devPreview) return null;

    const who = businessName ?? "the client";
    const text = viewAs.ended
        ? "This view has ended (it lasts about 2 hours). Open View as the merchant again from the staff tab."
        : viewAs.active
            ? `Viewing as ${who}, read only.${viewAs.placeholder ? " This merchant has no login yet." : ""}`
            : `Previewing as ${who}. Requests still use your staff login.`;

    const backToStaff = () => {
        if (devPreview) {
            setDevOuraRole("oura-staff");
            // A full load: the shell itself changes with the role.
            window.location.href = `${OURA_ROOT}/businesses`;
            return;
        }
        window.close();
        window.setTimeout(() => {
            if (!window.closed) setCloseRefused(true);
        }, 300);
    };

    const bar = (
        <div
            role="status"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-900"
        >
            <span className="inline-flex items-center gap-1.5">
                <EyeIcon aria-hidden className="h-3.5 w-3.5 shrink-0" />
                {text}
            </span>
            {!viewAs.ended && (closeRefused ? (
                <span className="text-amber-800">Close this tab to go back. Your staff tab is still open.</span>
            ) : (
                <button type="button" onClick={backToStaff} className="inline-flex items-center gap-1 rounded text-blue-600 hover:text-blue-800 hover:underline">
                    Back to staff view
                    <ArrowRightIcon aria-hidden className="h-3.5 w-3.5" />
                </button>
            ))}
        </div>
    );
    // Outside the merchant shell (no slot), in place above the page.
    return slot ? createPortal(bar, slot) : <div className="mb-4 overflow-hidden rounded-lg">{bar}</div>;
}

/**
 * Shows the server's sentence when a write is refused for being read-only.
 *
 * The services each answer a failed write with their own generic line, so
 * without this a refused write reads as "something went wrong". Renders
 * nothing; mount it once inside the SnackbarProvider.
 */
export function ViewAsRefusalNotice() {
    const snackbar = useSnackbar();
    useEffect(() => {
        const onRefused = (e: Event) => {
            const detail = (e as CustomEvent<string>).detail;
            snackbar.showError(detail || REFUSAL_FALLBACK);
        };
        window.addEventListener(VIEW_AS_REFUSED_EVENT, onRefused);
        return () => window.removeEventListener(VIEW_AS_REFUSED_EVENT, onRefused);
    }, [snackbar]);
    return null;
}
